'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    X, FolderOpen, Link as LinkIcon, PhoneCall, 
    MessageSquare, CheckCircle2, Users,
    FileText, Bot, Send, HardDrive, Activity,
    ArrowRight, Zap, Clock, GitBranch, RefreshCw,
    AlertTriangle, ChevronRight, ChevronLeft, Layers, BarChart3,
    Save, FolderSearch, Loader2, Check, Search,
    Ruler, FileSpreadsheet, Palette, Paperclip, Copy, Edit2, Trash2,
    Pin, ThumbsUp, Eye, Maximize, Minimize, Moon, Sun, Download,
    Network
} from 'lucide-react';
import EmojiIcon, { getFileIcon } from '@/components/EmojiIcon';
import OmniChatPanel from './OmniChatPanel';
import { NasProjectSelector } from './NasProjectSelector';

interface ProjectHistoryModalProps {
    isOpen: boolean;
    onClose: () => void;
    taskTitle?: string;
    projectName?: string;
    projectId?: string;
    date?: string;
    isDark?: boolean;
    onNavigateToFlow?: (projectName: string) => void;
}

interface ResolvedProject {
    key: string;
    fullName: string;
    nasId: string;
    nasIdPrefix: string;
    aliases: string[];
    exactNasPath?: string;
}

interface NasEvent {
    action: string;
    filename: string;
    category: string;
    summary: string;
    timestamp: string;
    created?: string;
    modified?: string;
    isModified?: boolean;
    size?: number;
}

interface FlowTask {
    id: string;
    title: string;
    status: string;
    dueDate: string | null;
    assignee: string | null;
    progress: number;
}

interface FlowProgress {
    projectId: string | null;
    projectName: string;
    total: number;
    done: number;
    inProgress: number;
    todo: number;
    completionRate: number;
    recentTasks: FlowTask[];
}

interface ScheduleUpcoming {
    title: string;
    date: string;
    status: string;
}

interface BoardData {
    resolved: boolean;
    project?: ResolvedProject;
    nasEvents?: NasEvent[];
    nasAiReport?: string;
    flowProgress?: FlowProgress;
    scheduleSummary?: {
        thisWeekCount: number;
        totalCount: number;
        upcomingTasks: ScheduleUpcoming[];
    };
    aiSummary?: string;
    memos?: { id: string; text: string; timestamp: string; }[];
    matchLayer?: string;
    message?: string;
}

// ── NAS Tree Renderer (recursive) ──
function NasTreeRenderer({ folders, files, depth, isDark, nasTreeCache, nasExpandedPaths, nasLoadingPaths, toggleTreeFolder, linkNasFolder, handleFilePreview, nasSearchQuery, onContextMenu }: {
    folders: {name: string; path: string}[];
    files: {name: string; path: string; ext: string; size: number}[];
    depth: number;
    isDark: boolean;
    nasTreeCache: Record<string, {folders: {name:string;path:string}[], files: {name:string;path:string;ext:string;size:number}[]}>;
    nasExpandedPaths: Set<string>;
    nasLoadingPaths: Set<string>;
    toggleTreeFolder: (path: string) => void;
    linkNasFolder: (path: string) => void;
    handleFilePreview: (file: {name: string; path: string; ext?: string}) => void;
    nasSearchQuery: string;
    onContextMenu?: (e: React.MouseEvent, path: string, name: string) => void;
}) {
    const indent = depth * 16;
    return (
        <>
            {folders.map((folder) => {
                const isExpanded = nasExpandedPaths.has(folder.path);
                const isLoading = nasLoadingPaths.has(folder.path);
                const children = nasTreeCache[folder.path];
                return (
                    <div key={folder.path}>
                        <div
                            className={`flex items-center gap-1.5 py-1.5 px-2 rounded-lg text-xs transition-colors group cursor-pointer ${
                                isDark ? 'hover:bg-white/10 text-gray-300' : 'hover:bg-gray-100 text-gray-700'
                            }`}
                            style={{ paddingLeft: `${8 + indent}px` }}
                            onClick={() => toggleTreeFolder(folder.path)}
                            draggable={true}
                            onDragStart={(e) => {
                                e.dataTransfer.setData('text/plain', JSON.stringify({ type: 'nas_folder', path: folder.path, name: folder.name }));
                            }}
                            onContextMenu={(e) => {
                                if (onContextMenu) onContextMenu(e, folder.path, folder.name);
                            }}
                        >
                            {isLoading ? (
                                <Loader2 className="w-3 h-3 animate-spin text-gray-400 flex-shrink-0" />
                            ) : (
                                <ChevronRight className={`w-3 h-3 flex-shrink-0 transition-transform duration-200 ${isDark ? 'text-gray-500' : 'text-gray-400'} ${isExpanded ? 'rotate-90' : ''}`} />
                            )}
                            <FolderOpen className={`w-3.5 h-3.5 flex-shrink-0 ${isExpanded ? 'text-amber-400' : 'text-amber-500/70'}`} />
                            <span className="truncate flex-1 font-medium" title={folder.name}>
                                {folder.name}
                            </span>
                            <button
                                onClick={(e) => { e.stopPropagation(); linkNasFolder(folder.path); }}
                                className="px-1.5 py-0.5 rounded text-[9px] bg-indigo-500 hover:bg-indigo-600 text-white opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                            >
                                연결
                            </button>
                        </div>
                        {isExpanded && children && (
                            <NasTreeRenderer
                                folders={children.folders}
                                files={children.files}
                                depth={depth + 1}
                                isDark={isDark}
                                nasTreeCache={nasTreeCache}
                                nasExpandedPaths={nasExpandedPaths}
                                nasLoadingPaths={nasLoadingPaths}
                                toggleTreeFolder={toggleTreeFolder}
                                linkNasFolder={linkNasFolder}
                                handleFilePreview={handleFilePreview}
                                nasSearchQuery={nasSearchQuery}
                                onContextMenu={onContextMenu}
                            />
                        )}
                    </div>
                );
            })}
            {files.map((file) => {
                const FileIcon = getFileIcon(file.ext);
                const sizeStr = file.size > 1048576  
                    ? `${(file.size / 1048576).toFixed(1)}MB`
                    : file.size > 1024 
                        ? `${(file.size / 1024).toFixed(0)}KB`
                        : `${file.size}B`;
                return (
                    <div
                        key={file.path}
                        onClick={() => handleFilePreview({ name: file.name, path: file.path, ext: file.ext })}
                        className={`flex items-center gap-2 py-1 px-2 rounded-lg text-xs transition-colors cursor-pointer ${
                            isDark ? 'hover:bg-white/5 text-gray-400' : 'hover:bg-gray-50 text-gray-600'
                        }`}
                        style={{ paddingLeft: `${22 + indent}px` }}
                        draggable={true}
                        onDragStart={(e) => {
                            e.dataTransfer.setData('text/plain', JSON.stringify({ type: 'nas_file', path: file.path, name: file.name }));
                        }}
                        onContextMenu={(e) => {
                            if (onContextMenu) onContextMenu(e, file.path, file.name);
                        }}
                    >
                        <span className="flex-shrink-0 flex items-center justify-center w-4">
                            <FileIcon className="w-3.5 h-3.5 text-slate-400" />
                        </span>
                        <span className="truncate flex-1" title={file.name}>{file.name}</span>
                        <span className={`text-[9px] flex-shrink-0 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{sizeStr}</span>
                    </div>
                );
            })}
        </>
    );
}

function timeAgo(isoStr: string): string {
    const diff = Date.now() - new Date(isoStr).getTime();
    const min = Math.floor(diff / 60000);
    if (min < 1) return '방금 전';
    if (min < 60) return `${min}분 전`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}시간 전`;
    const d = Math.floor(hr / 24);
    return `${d}일 전`;
}

function statusBadge(status: string, isDark: boolean) {
    const map: Record<string, { label: string; color: string; bg: string }> = {
        done: { label: '완료', color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20' },
        'in_progress': { label: '진행중', color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/20' },
        'in-progress': { label: '진행중', color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/20' },
        todo: { label: '대기', color: 'text-gray-400', bg: 'bg-gray-500/10 border-gray-500/20' },
        pending: { label: '대기', color: 'text-gray-400', bg: 'bg-gray-500/10 border-gray-500/20' },
        issue: { label: '이슈', color: 'text-rose-400', bg: 'bg-rose-500/10 border-rose-500/20' },
    };
    const s = map[status] || { label: status, color: 'text-gray-400', bg: 'bg-gray-500/10 border-gray-500/20' };
    return (
        <span className={`text-[10px] px-2 py-0.5 rounded-full border ${s.bg} ${s.color} font-bold`}>
            {s.label}
        </span>
    );
}

const TEAM_MEMBERS = [
    { id: 'bgc', name: '배경철 대표' },
    { id: 'khe', name: '기호율 이사' },
    { id: 'byj', name: '박윤지 이사' },
    { id: 'hjh', name: '황지혜 과장' },
    { id: 'htm', name: '황태문 과장' },
    { id: 'phw', name: '박현우 대리' },
    { id: 'ldy', name: '이다윤 디자이너' },
    { id: 'ghi', name: '길현일 디자이너' },
];


interface FlowPost {
  id: string; postType: string; title: string | null; content: string;
  authorName: string; authorDept: string | null; isPinned: boolean;
  likeCount: number; readCount: number; commentCount: number;
  attachments: Array<{ fileName: string; fileUrl: string }>;
  recentComments: Array<{ id: string; authorName: string; content: string; createdAt: string }>;
  createdAt: string;
}

function parseFlowContent(raw: string) {
  const result = { text: '', links: [] as any[], images: [] as any[], files: [] as any[] };
  if (!raw) return result;
  try {
    const parsed = JSON.parse(raw);
    if (parsed?.COMPS && Array.isArray(parsed.COMPS)) {
      for (const comp of parsed.COMPS) {
        const detail = comp.COMP_DETAIL || {};
        switch (comp.COMP_TYPE) {
          case 'TEXT': if (detail.CONTENTS) result.text += (result.text ? '\n' : '') + detail.CONTENTS; break;
          case 'LINK': if (detail.PREVIEW_LINK || detail.PREVIEW_CNTN) result.links.push({ url: detail.PREVIEW_LINK || detail.PREVIEW_CNTN || '', title: detail.PREVIEW_TTL || detail.PREVIEW_LINK || '링크' }); break;
          case 'IMAGE': if (detail.THUM_IMG_PATH || detail.FILE_DOWN_URL) result.images.push({ url: detail.THUM_IMG_PATH || detail.FILE_DOWN_URL || '', name: detail.FILE_NM || '이미지' }); break;
          case 'FILE': if (detail.FILE_DOWN_URL || detail.FILE_NM) result.files.push({ url: detail.FILE_DOWN_URL || '', name: detail.FILE_NM || '파일' }); break;
        }
      }
      return result;
    }
  } catch {}
  result.text = raw;
  return result;
}

function SchedulerProjectFeed({ projectId, isDark }: { projectId: string; isDark: boolean }) {
  const [posts, setPosts] = useState<FlowPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    fetch(`/api/flow/projects/${projectId}/posts?limit=30&sort=${sortOrder}`)
      .then(res => res.json())
      .then(data => {
        if (isMounted) {
          setPosts(data.posts || []);
          setLoading(false);
        }
      })
      .catch(() => {
        if (isMounted) setLoading(false);
      });
    return () => { isMounted = false; };
  }, [projectId, sortOrder]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-2">
        <h3 className={`text-lg font-bold flex items-center gap-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
          <MessageSquare className="w-5 h-5 text-indigo-500" />
          프로젝트 피드
        </h3>
        <button onClick={() => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')} 
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${isDark ? 'bg-white/5 hover:bg-white/10 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}>
          {sortOrder === 'desc' ? '최신순 (내림차순) ▼' : '과거순 (오름차순) ▲'}
        </button>
      </div>

      {posts.length === 0 ? (
        <div className={`text-center py-12 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
          <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-20" />
          <p className="text-sm">게시물이 없습니다.</p>
        </div>
      ) : (
        <div className="space-y-4 pr-2">
          {posts.map(post => {
            const parsed = parseFlowContent(post.content);
            return (
              <div key={post.id} className={`p-4 rounded-xl border ${isDark ? 'bg-[#1a1d24] border-gray-800' : 'bg-white border-gray-200 shadow-sm'}`}>
                {/* Header */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-sm">
                      {post.authorName.charAt(0)}
                    </div>
                    <div>
                      <p className={`text-sm font-semibold flex items-center gap-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                        {post.authorName}
                        {post.authorDept && <span className={`font-normal text-[10px] px-1.5 py-0.5 rounded ${isDark ? 'bg-white/10 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>{post.authorDept}</span>}
                      </p>
                      <p className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{timeAgo(post.createdAt)}</p>
                    </div>
                  </div>
                  {post.isPinned && <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold bg-amber-500/10 text-amber-500`}><Pin className="w-3 h-3 inline pb-0.5" /> 고정</span>}
                </div>
                
                {/* Content */}
                <div className="mb-3">
                  {post.title && <h4 className={`font-bold mb-1.5 text-sm ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{post.title}</h4>}
                  {parsed.text && <p className={`text-xs leading-relaxed whitespace-pre-wrap ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{parsed.text.trim()}</p>}
                  
                  {/* Attachments & Links */}
                  <div className="mt-3 space-y-1.5">
                    {parsed.links.map((link, i) => (
                      <a key={`link-${i}`} href={link.url} target="_blank" rel="noopener noreferrer" className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs truncate transition-colors ${isDark ? 'bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20' : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'}`}>
                        <span className="font-bold min-w-10">링크</span>
                        <span className="truncate opacity-80">{link.title}</span>
                      </a>
                    ))}
                    {[...parsed.images, ...parsed.files, ...post.attachments].map((file: any, i) => (
                      <div key={`file-${i}`} className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs truncate ${isDark ? 'bg-white/5 text-gray-400' : 'bg-gray-50 text-gray-500'}`}>
                        <Paperclip className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate">{file.fileName || file.name || '첨부파일'}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Footer Status */}
                <div className={`flex items-center gap-4 pt-3 mt-3 border-t ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
                  <span className={`text-[11px] flex items-center gap-1.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    <ThumbsUp className="w-3.5 h-3.5" /> {post.likeCount}
                  </span>
                  <span className={`text-[11px] flex items-center gap-1.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    <MessageSquare className="w-3.5 h-3.5" /> {post.commentCount}
                  </span>
                  <span className={`text-[11px] flex items-center gap-1.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    <Eye className="w-3.5 h-3.5" /> {post.readCount}
                  </span>
                </div>

                {/* Comments */}
                {post.recentComments.length > 0 && (
                  <div className={`mt-3 pt-3 border-t space-y-2 ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
                    {post.recentComments.map(c => {
                      const cParsed = parseFlowContent(c.content);
                      return (
                        <div key={c.id} className="flex gap-2.5 items-start">
                          <div className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold shrink-0 bg-gray-700 text-white">
                            {c.authorName.charAt(0)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className={`px-3 py-2 rounded-xl rounded-tl-none ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                              <span className={`text-[11px] font-bold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{c.authorName}</span>
                              <div className={`text-xs mt-0.5 whitespace-pre-wrap ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                {cParsed.text || c.content}
                              </div>
                            </div>
                            <p className={`text-[9px] mt-1 px-1 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{timeAgo(c.createdAt)}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function ProjectHistoryModal({
    isOpen,
    onClose,
    taskTitle,
    projectName,
    projectId,
    date,
    isDark: propIsDark = true,
    onNavigateToFlow,
}: ProjectHistoryModalProps) {
    const abortControllerRef = useRef<AbortController | null>(null);
    const [isDark, setIsDark] = useState(propIsDark);
    const [boardData, setBoardData] = useState<BoardData | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setIsDark(propIsDark);
    }, [propIsDark]);
    
    // ── 메모장 상태 (게시판 형태) ──
    const [memos, setMemos] = useState<{ id: string; text: string; timestamp: string; }[]>([]);
    const [newMemo, setNewMemo] = useState('');
    const [memoAuthor, setMemoAuthor] = useState('');
    const [editingMemoId, setEditingMemoId] = useState<string | null>(null);
    const [editMemoText, setEditMemoText] = useState('');
    const [copiedId, setCopiedId] = useState<string | null>(null);

    // ── Flow 업무 추가 요청 상태 ──
    const [newTaskTitle, setNewTaskTitle] = useState('');
    const [newTaskAssignee, setNewTaskAssignee] = useState('');
    const [taskCreating, setTaskCreating] = useState(false);

    // ── 프로젝트 이름 변경 ──
    const [isEditingProjectName, setIsEditingProjectName] = useState(false);
    const [editProjectName, setEditProjectName] = useState('');

    // 채팅창 사이즈 조절 상태
    const [chatWidth, setChatWidth] = useState(400);
    const [nasWidth, setNasWidth] = useState(350);
    const [nasContextMenu, setNasContextMenu] = useState<{x: number, y: number, path: string, name: string, isFolder?: boolean} | null>(null);
    const [nasClipboard, setNasClipboard] = useState<{type: 'copy' | 'cut', path: string, name: string} | null>(null);
    const [nasRenaming, setNasRenaming] = useState<{path: string, name: string} | null>(null);
    const [renameValue, setRenameValue] = useState('');
    const [chatNasPath, setChatNasPath] = useState<string | null>(null);

    const [activeSection, setActiveSection] = useState<'overview' | 'nas' | 'flow' | 'feed'>('overview');
    // ── 편집 모드: NAS 수동 연결 + 00_COWORK 저장 ──
    const [showNasBrowser, setShowNasBrowser] = useState(false);
    const [nasFolders, setNasFolders] = useState<{name: string; path: string}[]>([]);
    const [nasFiles, setNasFiles] = useState<{name: string; path: string; ext: string; size: number}[]>([]);
    const [currentNasPath, setCurrentNasPath] = useState('');
    const [nasBrowsing, setNasBrowsing] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [previewFile, setPreviewFile] = useState<{name: string; path: string; loading?: boolean; content?: string; url?: string; type?: 'image' | 'pdf' | 'other'} | null>(null);
    const [nasSearchQuery, setNasSearchQuery] = useState('');
    // ── NAS Tree State ──
    const [nasTreeCache, setNasTreeCache] = useState<Record<string, {folders: {name:string;path:string}[], files: {name:string;path:string;ext:string;size:number}[]}>>({}); 
    const [nasExpandedPaths, setNasExpandedPaths] = useState<Set<string>>(new Set());
    const [nasLoadingPaths, setNasLoadingPaths] = useState<Set<string>>(new Set());
    const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
    const [linkStatus, setLinkStatus] = useState<'idle' | 'linking' | 'linked' | 'error'>('idle');

    // NAS 폴더 목록 가져오기
    const browseNasFolders = useCallback(async (path: string = '', fallbackPath?: string) => {
        setNasBrowsing(true);
        try {
            const res = await fetch('/api/project-board/resolve', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'browse-nas', path })
            });
            if (res.ok) {
                const data = await res.json();
                const isEmpty = (!data.folders || data.folders.length === 0) && (!data.files || data.files.length === 0);
                if (isEmpty && fallbackPath) {
                    return browseNasFolders(fallbackPath);
                }
                setNasFolders(data.folders || []);
                setNasFiles(data.files || []);
                setCurrentNasPath(data.path || '');
                // Also cache root level in tree
                const rootKey = data.path || '';
                setNasTreeCache(prev => ({...prev, [rootKey]: { folders: data.folders || [], files: data.files || [] }}));
            } else {
                setNasFolders([]);
                setNasFiles([]);
            }
        } catch {
            setNasFolders([]);
            setNasFiles([]);
        } finally {
            setNasBrowsing(false);
        }
    }, []);

    // NAS Tree: 폴더 토글 (펼치기/접기)
    const toggleTreeFolder = useCallback(async (folderPath: string) => {
        // Already expanded → collapse
        if (nasExpandedPaths.has(folderPath)) {
            setNasExpandedPaths(prev => { const n = new Set(prev); n.delete(folderPath); return n; });
            return;
        }
        // Already cached → just expand
        if (nasTreeCache[folderPath]) {
            setNasExpandedPaths(prev => new Set(prev).add(folderPath));
            return;
        }
        // Fetch children
        setNasLoadingPaths(prev => new Set(prev).add(folderPath));
        try {
            const res = await fetch('/api/project-board/resolve', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'browse-nas', path: folderPath })
            });
            if (res.ok) {
                const data = await res.json();
                setNasTreeCache(prev => ({...prev, [folderPath]: { folders: data.folders || [], files: data.files || [] }}));
                setNasExpandedPaths(prev => new Set(prev).add(folderPath));
            }
        } catch { /* silently fail */ }
        setNasLoadingPaths(prev => { const n = new Set(prev); n.delete(folderPath); return n; });
    }, [nasExpandedPaths, nasTreeCache]);

    // NAS 수동 연결
    const linkNasFolder = useCallback(async (nasFolder: string) => {
        setLinkStatus('linking');
        try {
            const res = await fetch('/api/project-board/resolve', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'link-nas',
                    taskTitle,
                    projectName,
                    nasFolder,
                }),
            });
            if (res.ok) {
                setLinkStatus('linked');
                setShowNasBrowser(false);
                // 연결 후 보드 데이터 리프레시 (새 매핑 반영)
                setTimeout(() => {
                    setLinkStatus('idle');
                    fetchBoardData();
                }, 1500);
            } else {
                setLinkStatus('error');
            }
        } catch {
            setLinkStatus('error');
        }
    }, [taskTitle, projectName]);

    // 00_COWORK에 저장
    const saveToCowork = useCallback(async () => {
        if (!boardData?.project?.nasId) return;
        setSaveStatus('saving');
        try {
            const res = await fetch('/api/project-board/resolve', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'save-to-cowork',
                    nasId: boardData.project.nasId,
                    nasPath: boardData.project.exactNasPath,
                    projectName: boardData.project.fullName,
                    summary: boardData.aiSummary || '',
                }),
            });
            if (res.ok) {
                setSaveStatus('saved');
                setTimeout(() => setSaveStatus('idle'), 3000);
            } else {
                setSaveStatus('error');
            }
        } catch {
            setSaveStatus('error');
        }
    }, [boardData]);

    const handleRenameProject = async () => {
        if (!projectId || projectId === 'unassigned' || !editProjectName.trim()) return;
        try {
            const res = await fetch('/api/project-board/resolve', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'rename-project',
                    projectId,
                    newName: editProjectName.trim(),
                }),
            });
            if (res.ok) {
                setIsEditingProjectName(false);
                // Trigger reload to get the fresh projection name
                if (typeof window !== 'undefined') {
                    window.location.reload();
                }
            } else {
                alert('프로젝트명 변경에 실패했습니다.');
            }
        } catch (e) {
            alert('프로젝트명 변경 중 오류가 발생했습니다.');
            console.error(e);
        }
    };

    const saveMemoToServer = useCallback(async () => {
        if (!boardData?.project?.nasId || !newMemo.trim()) return;
        try {
            const res = await fetch('/api/project-board/resolve', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'save-memo',
                    nasId: boardData.project.nasId,
                    projectName: boardData.project.fullName,
                    memo: memoAuthor ? `[${memoAuthor}] ${newMemo}` : newMemo,
                }),
            });
            const data = await res.json();
            if (res.ok && data.memo) {
                setMemos(prev => [data.memo, ...prev]);
                setNewMemo('');
            } else {
                alert('메모 저장에 실패했습니다.');
            }
        } catch {
            alert('메모 저장 오류');
        }
    }, [boardData, newMemo]);

    const updateMemoToServer = useCallback(async (id: string) => {
        if (!editMemoText.trim()) return;
        try {
            const res = await fetch('/api/project-board/resolve', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'update-memo', memoId: id, memo: editMemoText }),
            });
            if (res.ok) {
                setMemos(prev => prev.map(m => m.id === id ? { ...m, text: editMemoText, timestamp: new Date().toISOString() } : m));
                setEditingMemoId(null);
            } else {
                alert('메모 수정에 실패했습니다.');
            }
        } catch { alert('메모 수정 오류'); }
    }, [editMemoText]);

    const deleteMemoToServer = useCallback(async (id: string) => {
        if (!confirm('정말 메모를 삭제하시겠습니까?')) return;
        try {
            const res = await fetch('/api/project-board/resolve', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'delete-memo', memoId: id }),
            });
            if (res.ok) {
                setMemos(prev => prev.filter(m => m.id !== id));
            } else {
                alert('메모 삭제에 실패했습니다.');
            }
        } catch { alert('메모 삭제 오류'); }
    }, []);

    const copyToClipboard = (id: string, text: string) => {
        navigator.clipboard.writeText(text);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
    };


    const handleFilePreview = useCallback(async (file: {name: string; path: string; ext?: string}) => {
        const fileExt = file.ext || file.name.split('.').pop()?.toLowerCase() || '';
        
        let type: 'image' | 'pdf' | 'other' = 'other';
        if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExt)) type = 'image';
        else if (fileExt === 'pdf') type = 'pdf';
        
        if (type !== 'other') {
            const gateway = (process.env.NEXT_PUBLIC_NAS_GATEWAY || 'https://grid.snapcodex.com').trim();
            // 최적화: Date.now() 타임스탬프를 제거하여 브라우저의 이미지/PDF 캐싱 허용
            const previewUrl = `${gateway}/api/nas-raw?path=${encodeURIComponent(file.path)}`;
            setPreviewFile({ name: file.name, path: file.path, loading: false, url: previewUrl, type });
            return;
        }

        setPreviewFile({ name: file.name, path: file.path, loading: true });
        try {
            const r = await fetch('/api/precache', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ filePath: file.path }),
            });
            const d = await r.json();
            setPreviewFile(prev => prev ? { ...prev, loading: false, content: d.summary || d.text || '미리보기 불가' } : null);
        } catch {
            setPreviewFile(prev => prev ? { ...prev, loading: false, content: '미리보기 로드 실패' } : null);
        }
    }, []);

    const handleFileDownload = (file: {name: string; path: string}) => {
        const downloadUrl = `${(process.env.NEXT_PUBLIC_NAS_GATEWAY || 'https://grid.snapcodex.com').trim()}/api/nas-raw?filePath=${encodeURIComponent(file.path)}&download=true`;
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = file.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const fetchBoardData = useCallback(async (isSilent = false) => {
        if (!taskTitle && !projectName) return;
        
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        const controller = new AbortController();
        abortControllerRef.current = controller;

        if (!isSilent) setLoading(true);
        setError(null);
        
        const maxRetries = 3;
        for (let attempt = 0; attempt <= maxRetries; attempt++) {
            try {
                const params = new URLSearchParams({
                    taskTitle: taskTitle || '',
                    projectName: projectName || '',
                    date: date || '',
                });
                const res = await fetch(`/api/project-board/resolve?${params}`, {
                    signal: controller.signal
                });
                
                if (!res.ok) {
                    if (res.status === 503 && attempt < maxRetries) {
                        const delay = Math.pow(2, attempt) * 2000;
                        console.warn(`[ProjectHistoryModal] 503 Service Unavailable. Retrying ${attempt + 1}/${maxRetries} in ${delay}ms...`);
                        await new Promise(r => setTimeout(r, delay));
                        continue;
                    }
                    throw new Error(`HTTP error! status: ${res.status}`);
                }
                
                const data = await res.json();
                
                // NAS 이벤트가 아직 로딩 중임을 표시하기 위해 null 처리
                if (data.project?.exactNasPath) {
                    data.nasEvents = null; 
                    data.nasAiReport = 'NAS 데이터를 실시간 스캔 중입니다...';
                }
                
                setBoardData(data);
                if (data.memos) setMemos(data.memos);
                
                // [PATCH 2026-04-23] NAS 스캔 병렬 비동기 호출
                if (data.project?.exactNasPath) {
                    fetch(`/api/nas-recent-events?exact=${encodeURIComponent(data.project.exactNasPath)}&fallback=${encodeURIComponent(data.project.fallbackNasPath || '')}`, { signal: controller.signal })
                    .then(r => {
                        if (!r.ok) throw new Error('NAS scan request failed');
                        return r.json();
                    })
                    .then(nasData => {
                        setBoardData((prev: any) => prev ? { ...prev, nasEvents: nasData.nasEvents || [], nasAiReport: nasData.nasAiReport || '' } : prev);
                    }).catch(err => {
                        if (err.name !== 'AbortError') {
                            console.error('NAS scan error:', err);
                            setBoardData((prev: any) => prev ? { ...prev, nasEvents: [], nasAiReport: 'NAS 파일 스캔 중 오류가 발생했습니다.' } : prev);
                        }
                    });
                }
                
                break; // 성공 시 루프 탈출
            } catch (err: any) {
                if (err.name === 'AbortError') return;
                
                if (attempt < maxRetries) {
                    const delay = Math.pow(2, attempt) * 2000;
                    console.warn(`[ProjectHistoryModal] Network error. Retrying ${attempt + 1}/${maxRetries} in ${delay}ms...`);
                    await new Promise(r => setTimeout(r, delay));
                    continue;
                }
                
                setError('프로젝트 데이터를 불러올 수 없습니다.');
                console.error('[ProjectHistoryModal] fetch error:', err);
            }
        }
        
        if (abortControllerRef.current === controller) {
            setLoading(false);
        }
    }, [taskTitle, projectName, date]);

    const handleCreateFlowTask = useCallback(async () => {
        if (!newTaskTitle.trim() || !newTaskAssignee || !boardData?.flowProgress?.projectId) return;
        setTaskCreating(true);
        try {
            const res = await fetch(`/api/flow/projects/${boardData.flowProgress.projectId}/tasks`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    title: newTaskTitle,
                    assigneeName: newTaskAssignee,
                    requesterName: memoAuthor || '스케줄러',
                    status: 'todo',
                })
            });
            if (res.ok) {
                setNewTaskTitle('');
                setNewTaskAssignee('');
                fetchBoardData(); // reload board to see new task
            } else {
                alert('업무 요청에 실패했습니다.');
            }
        } catch (e) {
            alert('업무 요청 중 오류가 발생했습니다.');
        } finally {
            setTaskCreating(false);
        }
    }, [newTaskTitle, newTaskAssignee, boardData, memoAuthor, fetchBoardData]);


    useEffect(() => {
        let interval: NodeJS.Timeout | null = null;
        const onVisibility = () => {
            // 탭이 다시 보이면 즉시 한 번 조용히 fetch (사용자 체감 즉각성)
            if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
                fetchBoardData(true);
            }
        };
        if (isOpen) {
            fetchBoardData();
            // 기본 3패널 시스템으로 오픈 (전체화면 + NAS 탐색기)
            setIsFullscreen(true);
            setShowNasBrowser(false);

            // 실시간 폴링 간격: 30초. 탭 숨김 상태면 건너뜀. (서버 캐시 TTL 60초와 정합)
            interval = setInterval(() => {
                if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
                    return;
                }
                fetchBoardData(true); // silent fetch
            }, 30_000);

            if (typeof document !== 'undefined') {
                document.addEventListener('visibilitychange', onVisibility);
            }
        } else {
            setBoardData(null);
            setError(null);
            setIsFullscreen(false);
            setShowNasBrowser(false);
            if (interval) clearInterval(interval);
        }
        return () => {
            if (interval) clearInterval(interval);
            if (typeof document !== 'undefined') {
                document.removeEventListener('visibilitychange', onVisibility);
            }
            // 컴포넌트 언마운트/의존성 변경 시 in-flight 요청 취소
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
                abortControllerRef.current = null;
            }
        };
    }, [isOpen, fetchBoardData]);

    // Reset state when project changes
    useEffect(() => {
        setBoardData(null);
        setNasFolders([]);
        setNasFiles([]);
        setCurrentNasPath('');
        setNasTreeCache({});
        setNasExpandedPaths(new Set());
        setPreviewFile(null);
        setChatNasPath(null);
    }, [projectId, projectName]);

    if (!isOpen) return null;

    const resolved = boardData?.resolved ?? false;
    const project = boardData?.project;
    const nasEvents = boardData?.nasEvents;
    const flowProgress = boardData?.flowProgress;
    const scheduleSummary = boardData?.scheduleSummary;
    const aiSummary = boardData?.aiSummary || '';
    const activeProject = project?.fullName || projectName || '선택된 프로젝트';

    return (
        <AnimatePresence>
            <div className={`fixed inset-0 z-[100] flex items-center justify-center ${isFullscreen ? 'p-0' : 'p-4'}`}>
                <motion.div 
                    initial={{ opacity: 0 }} 
                    animate={{ opacity: 1 }} 
                    exit={{ opacity: 0 }} 
                    className="absolute inset-0 bg-black/60 backdrop-blur-sm" 
                    onClick={onClose} 
                />
                
                <motion.div
                    initial={{ scale: 0.95, opacity: 0, y: 20 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.95, opacity: 0, y: 20 }}
                    className={`relative w-full flex flex-col shadow-2xl overflow-hidden transition-all duration-300 ${
                        isFullscreen 
                            ? 'max-w-[100vw] h-[100dvh] rounded-none' 
                            : 'max-w-5xl h-[80vh] rounded-2xl'
                    } ${isDark ? 'bg-[#11131a] border border-white/10' : 'bg-white border border-gray-200'}`}
                >
                    {/* ═══ Header ═══ */}
                    <div className={`p-4 sm:p-5 flex items-center justify-between border-b ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'}`}>
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl shadow-lg">
                                <FolderOpen className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <div className="flex items-center gap-3">
                                    {isEditingProjectName ? (
                                        <div className="flex items-center gap-2">
                                            <input 
                                                autoFocus
                                                type="text" 
                                                value={editProjectName}
                                                onChange={e => setEditProjectName(e.target.value)}
                                                onKeyDown={e => {
                                                    if (e.key === 'Enter') handleRenameProject();
                                                    if (e.key === 'Escape') setIsEditingProjectName(false);
                                                }}
                                                className={`text-xl font-bold px-2 py-1 rounded bg-transparent border-b outline-none ${isDark ? 'text-white border-white/30 focus:border-indigo-400' : 'text-gray-900 border-gray-300 focus:border-indigo-500'}`}
                                            />
                                            <button onClick={handleRenameProject} className="p-1.5 rounded-lg bg-indigo-500 hover:bg-indigo-600 text-white transition-colors">
                                                <Check className="w-4 h-4" />
                                            </button>
                                            <button onClick={() => setIsEditingProjectName(false)} className="p-1.5 rounded-lg bg-gray-500 hover:bg-gray-600 text-white transition-colors">
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ) : (
                                        <h2 className={`text-2xl font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                            {activeProject}
                                            {projectId && projectId !== 'unassigned' && (
                                                <button 
                                                    onClick={() => {
                                                        setEditProjectName(activeProject);
                                                        setIsEditingProjectName(true);
                                                    }}
                                                    className={`p-1 rounded opacity-50 hover:opacity-100 transition-opacity ${isDark ? 'hover:bg-white/10' : 'hover:bg-black/5'}`}
                                                    title="프로젝트 이름 수정"
                                                >
                                                    <Edit2 className="w-4 h-4" />
                                                </button>
                                            )}
                                        </h2>
                                    )}
                                    {resolved && project && (() => {
                                        const layer = boardData?.matchLayer || '';
                                        const isL3 = layer.startsWith('L3');
                                        const isL2 = layer.startsWith('L2_manual');
                                        
                                        let layerLabel = '⚡ AI 자동 연결';
                                        if (isL3) layerLabel = '🧠 AI 임베딩';
                                        else if (isL2) {
                                            if (layer.includes('expanded')) {
                                                const expandedAlias = layer.split('_expanded_')[1] || '';
                                                layerLabel = `👤 동적 별칭 (${expandedAlias.length > 5 ? expandedAlias.slice(0,5)+'..' : expandedAlias})`;
                                            } else {
                                                layerLabel = '👤 수동 연결';
                                            }
                                        }

                                        return (
                                            <span className={`text-[10px] px-2.5 py-1 rounded-lg font-bold ${
                                                isL3 ? (isDark ? 'bg-purple-500/10 text-purple-400 border border-purple-500/20' : 'bg-purple-50 text-purple-600 border border-purple-200')
                                                : isL2 ? (isDark ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' : 'bg-amber-50 text-amber-600 border border-amber-200')
                                                : (isDark ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-emerald-50 text-emerald-600 border border-emerald-200')
                                            }`}>
                                                <Zap className="w-3 h-3 inline -mt-0.5 mr-0.5" />
                                                {layerLabel} · {project.nasIdPrefix}
                                            </span>
                                        );
                                    })()}
                                </div>
                                <p className={`text-sm mt-1 flex items-center gap-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                    <span className={`px-2 py-0.5 rounded text-[11px] font-bold ${isDark ? 'bg-indigo-500/20 text-indigo-400 border border-indigo-500/20' : 'bg-indigo-100 text-indigo-600'}`}>
                                        {date}
                                    </span>
                                    <span>선택 태스크: {taskTitle}</span>
                                    {project?.nasId && (
                                        <button
                                            onClick={() => {
                                                setActiveSection('nas');
                                                setShowNasBrowser(true);
                                                if (project?.exactNasPath) {
                                                    browseNasFolders(project.exactNasPath);
                                                } else {
                                                    const yyMatch = project.nasId.match(/^ID_(\d{2})/);
                                                    const yy = yyMatch ? yyMatch[1] : '25';
                                                    browseNasFolders(`B_DESIGN/[20${yy}_PROJECT]/${project.nasId}`, `B_DESIGN/${project.nasId}`);
                                                }
                                            }}
                                            className={`text-[10px] px-2 py-0.5 rounded cursor-pointer transition-all hover:scale-105 active:scale-95 ${isDark ? 'bg-purple-500/10 text-purple-400 border border-purple-500/20 hover:bg-purple-500/20 hover:text-purple-300' : 'bg-purple-50 text-purple-600 hover:bg-purple-100'}`}
                                            title="클릭하여 프로젝트 NAS 폴더 열기"
                                        >
                                            <EmojiIcon emoji="📂" className="w-3 h-3 inline pb-0.5" /> {project.nasId} →
                                        </button>
                                    )}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setIsDark(!isDark)}
                                className={`p-2 rounded-xl border transition-colors ${
                                    isDark
                                        ? 'bg-gray-800 text-yellow-400 border-gray-700 hover:bg-gray-700'
                                        : 'bg-white text-indigo-500 border-gray-200 hover:bg-gray-50'
                                }`}
                                title="테마 변경"
                            >
                                {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                            </button>
                            {/* 플로우 대시보드 열기 버튼 */}
                            {onNavigateToFlow && (
                                <button
                                    onClick={() => {
                                        onClose();
                                        onNavigateToFlow(projectName || '');
                                    }}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                                        isDark
                                            ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30 hover:bg-purple-500/30'
                                            : 'bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-100'
                                    }`}
                                    title="플로우 프로젝트 대시보드 열기"
                                >
                                    <Network className="w-3.5 h-3.5" />
                                    플로우 대시보드
                                </button>
                            )}
                            {/* 00_COWORK 저장 버튼 */}
                            {resolved && boardData?.project?.nasId && (
                                <button
                                    onClick={saveToCowork}
                                    disabled={saveStatus === 'saving'}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                                        saveStatus === 'saved'
                                            ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                                            : saveStatus === 'error'
                                            ? 'bg-rose-500/20 text-rose-400 border border-rose-500/30'
                                            : isDark
                                            ? 'bg-indigo-500/20 text-indigo-400 border border-indigo-500/30 hover:bg-indigo-500/30'
                                            : 'bg-indigo-50 text-indigo-600 border border-indigo-200 hover:bg-indigo-100'
                                    }`}
                                    title="프로젝트 요약을 NAS COWORK에 저장"
                                >
                                    {saveStatus === 'saving' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> :
                                     saveStatus === 'saved' ? <Check className="w-3.5 h-3.5" /> :
                                     <Save className="w-3.5 h-3.5" />}
                                    {saveStatus === 'saved' ? 'COWORK 저장됨' :
                                     saveStatus === 'error' ? '저장 실패' :
                                     saveStatus === 'saving' ? '저장 중...' :
                                     'COWORK 저장'}
                                </button>
                            )}
                            {/* NAS 수동 연결 버튼 (항상 노출 - 오매핑 시 수정 가능하도록) */}
                                <button
                                    onClick={() => { 
                                        setShowNasBrowser(prev => {
                                            const n = !prev;
                                            if (n) {
                                                if (boardData?.project?.exactNasPath) {
                                                    browseNasFolders(boardData.project.exactNasPath);
                                                } else if (boardData?.project?.nasId) {
                                                    const yyMatch = boardData.project.nasId.match(/^ID_(\d{2})/);
                                                    const yy = yyMatch ? yyMatch[1] : '25';
                                                    browseNasFolders(`B_DESIGN/[20${yy}_PROJECT]/${boardData.project.nasId}`, `B_DESIGN/${boardData.project.nasId}`);
                                                } else {
                                                    browseNasFolders('');
                                                }
                                            }
                                            return n;
                                        });
                                    }}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                                        linkStatus === 'linked'
                                            ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                                            : resolved
                                            ? isDark
                                                ? 'bg-gray-700/50 text-gray-300 border border-gray-600 hover:bg-gray-600/50 hover:text-white'
                                                : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'
                                            : isDark
                                                ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30'
                                                : 'bg-amber-50 text-amber-600 border border-amber-200 hover:bg-amber-100'
                                    }`}
                                    title={'NAS 폴더 수동 연결'}
                                >
                                    <FolderSearch className="w-3.5 h-3.5" />
                                    {'NAS 폴더 수동 연결'}
                                </button>
                            <button
                                onClick={() => fetchBoardData()}
                                className={`p-2 rounded-xl transition-colors ${isDark ? 'hover:bg-white/10 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-500'}`}
                                title="새로고침"
                            >
                                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                            </button>
                            <button
                                onClick={() => setIsFullscreen(!isFullscreen)}
                                className={`p-2 rounded-xl transition-colors ${isDark ? 'hover:bg-white/10 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-500'}`}
                                title={isFullscreen ? "축소" : "전체화면"}
                            >
                                {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
                            </button>
                            <button 
                                onClick={onClose} 
                                className={`p-2 rounded-xl transition-colors ${isDark ? 'hover:bg-white/10 text-gray-400 hover:text-white' : 'hover:bg-gray-200 text-gray-500'}`}
                            >
                                <X className="w-6 h-6" />
                            </button>

                        </div>
                    </div>

                    {/* ═══ Main Content Grid ═══ */}
                    <div className="flex-1 overflow-hidden flex flex-col lg:flex-row">
                        
                        {/* ═══ Left Column: Main Board ═══ */}
                        <div className={`flex-1 p-5 overflow-y-auto space-y-5 ${isDark ? 'border-none' : 'border-r border-gray-100'}`}>
                            
                            {/* Loading / Error */}
                            {loading && !boardData && (
                                <div className="flex items-center justify-center py-20">
                                    <div className="flex flex-col items-center gap-3">
                                        <RefreshCw className="w-8 h-8 text-indigo-500 animate-spin" />
                                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>프로젝트 데이터 통합 중...</p>
                                    </div>
                                </div>
                            )}
                            
                            {error && (
                                <div className={`p-4 rounded-xl border ${isDark ? 'bg-rose-500/5 border-rose-500/20' : 'bg-rose-50 border-rose-200'}`}>
                                    <p className="text-rose-500 text-sm">{error}</p>
                                </div>
                            )}
                            {boardData && (
                                <>
                                    {/* ═══ AI 프로젝트 연결 확인 배너 ═══ */}
                                    {resolved && project && (
                                        <motion.div
                                            initial={{ opacity: 0, y: -10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            className={`p-4 rounded-xl border ${isDark ? 'bg-gradient-to-r from-indigo-500/5 to-purple-500/5 border-indigo-500/20' : 'bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200'}`}
                                        >
                                            <div className="flex items-start gap-3">
                                                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                                                    <Bot className="w-5 h-5 text-white" />
                                                </div>
                                                <div className="flex-1">
                                                    <h4 className={`font-bold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                                        AI 프로젝트 자동 연결 완료
                                                    </h4>
                                                    <p className={`text-xs mt-1 leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                                        &quot;{projectName}&quot; 스케줄이 <strong className="text-indigo-400">{project.fullName}</strong> (NAS: <code className="text-purple-400">{project.nasIdPrefix}</code>)와 
                                                        동일한 프로젝트임을 인식했습니다. NAS 파일 변경, Flow 진행현황, 스케줄이 통합 표시됩니다.
                                                    </p>
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}

                                    {!resolved && (
                                        <div className={`p-4 rounded-xl border ${isDark ? 'bg-amber-500/5 border-amber-500/20' : 'bg-amber-50 border-amber-200'}`}>
                                            <div className="flex items-center gap-2">
                                                <AlertTriangle className="w-5 h-5 text-amber-500" />
                                                <p className={`text-sm ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>
                                                    이 태스크의 NAS 프로젝트를 자동 매칭할 수 없습니다. 프로젝트 별칭을 추가하거나, 직접 NAS 폴더를 연결해주세요.
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {/* ═══ Section Tabs ═══ */}
                                    <div className={`flex gap-1 p-1 rounded-xl ${isDark ? 'bg-white/[0.03]' : 'bg-gray-100'}`}>
                                        {[
                                            { key: 'overview' as const, label: '통합 현황', icon: BarChart3 },
                                            { key: 'nas' as const, label: 'NAS 파일', icon: HardDrive },
                                            { key: 'flow' as const, label: 'Flow 태스크', icon: GitBranch },
                                            { key: 'feed' as const, label: '프로젝트 피드', icon: MessageSquare },
                                        ].map(tab => (
                                            <button
                                                key={tab.key}
                                                onClick={() => setActiveSection(tab.key)}
                                                className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                                                    activeSection === tab.key
                                                        ? isDark ? 'bg-indigo-500/20 text-indigo-300 shadow-sm' : 'bg-white text-indigo-600 shadow-sm'
                                                        : isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
                                                }`}
                                            >
                                                <tab.icon className="w-3.5 h-3.5" />
                                                {tab.label}
                                            </button>
                                        ))}
                                    </div>

                                    {/* ═══ OVERVIEW SECTION ═══ */}
                                    {activeSection === 'overview' && (
                                        <div className="space-y-5">
                                            {/* KPI Dashboard */}
                                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                                                <div className={`p-4 rounded-xl border ${isDark ? 'bg-white/[0.02] border-white/10' : 'bg-gray-50 border-gray-200'}`}>
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <Activity className="w-4 h-4 text-emerald-500" />
                                                        <span className={`text-xs font-bold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Flow 진행률</span>
                                                    </div>
                                                    <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                                        {flowProgress?.completionRate ?? 0}
                                                        <span className="text-xs font-normal text-gray-500 ml-0.5">%</span>
                                                    </div>
                                                    {flowProgress && flowProgress.total > 0 && (
                                                        <div className="mt-2 w-full h-1.5 rounded-full bg-gray-700/30 overflow-hidden">
                                                            <div
                                                                className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-green-400 transition-all"
                                                                style={{ width: `${flowProgress.completionRate}%` }}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                                <div className={`p-4 rounded-xl border ${isDark ? 'bg-white/[0.02] border-white/10' : 'bg-gray-50 border-gray-200'}`}>
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <Layers className="w-4 h-4 text-blue-500" />
                                                        <span className={`text-xs font-bold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Flow 태스크</span>
                                                    </div>
                                                    <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                                        {flowProgress?.total ?? 0}
                                                        <span className="text-xs font-normal text-gray-500 ml-0.5">건</span>
                                                    </div>
                                                    <div className={`text-[10px] mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                                        완료 {flowProgress?.done ?? 0} / 진행 {flowProgress?.inProgress ?? 0}
                                                    </div>
                                                </div>
                                                <div className={`p-4 rounded-xl border ${isDark ? 'bg-white/[0.02] border-white/10' : 'bg-gray-50 border-gray-200'}`}>
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <HardDrive className="w-4 h-4 text-purple-500" />
                                                        <span className={`text-xs font-bold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>NAS 변경</span>
                                                    </div>
                                                    <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                                        {nasEvents?.length || 0}
                                                        <span className="text-xs font-normal text-gray-500 ml-0.5">건</span>
                                                    </div>
                                                    <div className={`text-[10px] mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                                        최근 감지 파일
                                                    </div>
                                                </div>
                                                <div className={`p-4 rounded-xl border ${isDark ? 'bg-white/[0.02] border-white/10' : 'bg-gray-50 border-gray-200'}`}>
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <Clock className="w-4 h-4 text-amber-500" />
                                                        <span className={`text-xs font-bold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>이번 주 일정</span>
                                                    </div>
                                                    <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                                        {scheduleSummary?.thisWeekCount ?? 0}
                                                        <span className="text-xs font-normal text-gray-500 ml-0.5">건</span>
                                                    </div>
                                                    <div className={`text-[10px] mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                                        향후 4주: {scheduleSummary?.totalCount ?? 0}건
                                                    </div>
                                                </div>
                                            </div>

                                            {/* AI Summary */}
                                            {aiSummary && (
                                                <div className={`p-4 rounded-xl border ${isDark ? 'bg-indigo-500/5 border-indigo-500/20' : 'bg-indigo-50 border-indigo-200'}`}>
                                                    <h3 className={`text-sm font-bold flex items-center gap-2 mb-3 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                                                        <Bot className="w-4 h-4 text-indigo-500" />
                                                        AI 진행현황 요약 보고
                                                    </h3>
                                                    <pre className={`text-xs leading-relaxed whitespace-pre-wrap font-sans ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                                        {aiSummary}
                                                    </pre>
                                                </div>
                                            )}

                                            {/* Upcoming Schedule */}
                                            {scheduleSummary && scheduleSummary.upcomingTasks.length > 0 && (
                                                <div>
                                                    <h3 className={`text-sm font-bold flex items-center gap-2 mb-3 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                                                        <Clock className="w-4 h-4 text-amber-500" />
                                                        예정 스케줄
                                                    </h3>
                                                    <div className="space-y-1.5">
                                                        {scheduleSummary.upcomingTasks.map((t, i) => (
                                                            <div key={i} className={`flex items-center gap-3 px-3 py-2 rounded-lg ${isDark ? 'bg-white/[0.02] hover:bg-white/[0.04]' : 'bg-gray-50 hover:bg-gray-100'} transition-colors`}>
                                                                <span className={`text-xs font-mono w-12 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{t.date.slice(5)}</span>
                                                                <span className={`flex-1 text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{t.title}</span>
                                                                {statusBadge(t.status, isDark)}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* ═══ NAS SECTION ═══ */}
                                    {activeSection === 'nas' && (
                                        <div className="space-y-4">
                                            <div className="flex items-center justify-between">
                                                <h3 className={`text-lg font-bold flex items-center gap-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                                                    <HardDrive className="w-5 h-5 text-purple-500" />
                                                    NAS 파일 변경 이력
                                                    {project?.nasId && (
                                                        <span className={`text-[10px] font-normal px-2 py-0.5 rounded ${isDark ? 'bg-purple-500/10 text-purple-400' : 'bg-purple-50 text-purple-600'}`}>
                                                            {project.nasId}
                                                        </span>
                                                    )}
                                                </h3>
                                                {project?.nasId && (
                                                    <button
                                                        onClick={() => {
                                                            let pathStr = '';
                                                            if (project?.exactNasPath) {
                                                                pathStr = project.exactNasPath;
                                                            } else {
                                                                const yyMatch = project.nasId.match(/^ID_(\d{2})/);
                                                                const yy = yyMatch ? yyMatch[1] : '25';
                                                                pathStr = `B_DESIGN/[20${yy}_PROJECT]/${project.nasId}`;
                                                            }
                                                            setChatNasPath(pathStr);
                                                            setShowNasBrowser(true);
                                                            browseNasFolders(pathStr, `B_DESIGN/${project.nasId}`);
                                                        }}
                                                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                                                            isDark
                                                                ? 'bg-purple-500/15 text-purple-400 border border-purple-500/30 hover:bg-purple-500/25'
                                                                : 'bg-purple-50 text-purple-600 border border-purple-200 hover:bg-purple-100'
                                                        }`}
                                                    >
                                                        <FolderOpen className="w-3.5 h-3.5" />
                                                        NAS 폴더 열기
                                                    </button>
                                                )}
                                            </div>

                                            {boardData?.nasAiReport && (
                                                <div className={`mt-4 p-4 rounded-xl border flex items-start gap-3 ${isDark ? 'bg-indigo-500/5 border-indigo-500/20' : 'bg-indigo-50 border-indigo-100'}`}>
                                                    <Bot className="w-5 h-5 text-indigo-400 shrink-0 mt-0.5" />
                                                    <div className="flex-1 text-xs">
                                                        <span className={`font-bold block mb-1 ${isDark ? 'text-indigo-300' : 'text-indigo-800'}`}>🤖 AI 분석 요약</span>
                                                        <div className={`leading-relaxed whitespace-pre-wrap ${isDark ? 'text-indigo-200/80' : 'text-indigo-700/80'}`}>
                                                            {boardData.nasAiReport}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                            
                                            {previewFile && (
                                                <div className={`mt-4 border rounded-xl overflow-hidden shadow-inner flex flex-col ${isDark ? 'bg-[#0d0f14] border-gray-800' : 'bg-gray-50 border-gray-200'}`}>
                                                    <div className={`flex items-center justify-between px-4 py-2 border-b shrink-0 ${isDark ? 'bg-[#11131a] border-gray-800' : 'bg-white border-gray-200'}`}>
                                                        <div className="flex items-center gap-2">
                                                            <Eye className={`w-4 h-4 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
                                                            <span className={`text-sm font-bold truncate ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                                                                {previewFile.name} 미리보기
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <button 
                                                                onClick={() => handleFileDownload({ name: previewFile.name, path: previewFile.path })}
                                                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-bold text-amber-600 dark:text-amber-400 ${isDark ? 'bg-amber-500/10 hover:bg-amber-500/20' : 'bg-amber-50 hover:bg-amber-100'}`}
                                                            >
                                                                <Download className="w-3.5 h-3.5" />
                                                                다운로드
                                                            </button>
                                                            <button onClick={() => setPreviewFile(null)} className={`p-1 rounded-md ${isDark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}>
                                                                <X className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="relative w-full h-[600px] flex items-center justify-center bg-black/5 overflow-auto">
                                                        {previewFile.loading ? (
                                                            <div className="flex flex-col items-center gap-3">
                                                                <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
                                                                <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>파일 내용을 읽어오는 중...</span>
                                                            </div>
                                                        ) : previewFile.url ? (
                                                            previewFile.type === 'pdf' ? (
                                                                <iframe key={previewFile.url} src={previewFile.url} className="w-full h-full border-0 animate-in fade-in duration-300" title="PDF Preview" />
                                                            ) : (
                                                                <img key={previewFile.url} src={previewFile.url} className="max-w-full max-h-full object-contain animate-in fade-in duration-300" alt="Preview" />
                                                            )
                                                        ) : previewFile.content ? (
                                                            <pre className={`w-full h-full p-4 overflow-auto text-xs whitespace-pre-wrap font-mono ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                                                {previewFile.content}
                                                            </pre>
                                                        ) : (
                                                            <span className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>미리보기를 지원하지 않는 형식입니다.</span>
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            {nasEvents === null ? (
                                                <div className={`text-center py-12 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                                    <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-cyan-500/50" />
                                                    <p className="text-sm">NAS 파일 현황을 스캔 중입니다...</p>
                                                    <p className="text-xs mt-1 text-gray-500">대용량 폴더의 경우 2~3초 소요될 수 있습니다.</p>
                                                </div>
                                            ) : (!nasEvents || nasEvents.length === 0) ? (
                                                <div className={`text-center py-12 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                                    <HardDrive className="w-12 h-12 mx-auto mb-3 opacity-20" />
                                                    <p className="text-sm">NAS 파일 변경 이력이 없습니다.</p>
                                                    <p className="text-xs mt-1 text-gray-500">지정된 경로에 폴더가 존재하지 않거나 파일이 0개입니다.</p>
                                                    <p className="text-[10px] mt-2 text-cyan-500/80 break-all">{project?.nasIdPrefix}</p>
                                                </div>
                                            ) : (
                                                <div className="space-y-2 mt-4">
                                                    {nasEvents.map((evt, i) => {
                                                        const isNew = evt.action === 'file_created';
                                                        const isMod = evt.action === 'file_modified' || evt.isModified;
                                                        const isDel = evt.action === 'file_deleted';
                                                        const isListed = evt.action === 'file_listed';
                                                        
                                                        // Determine item coloring
                                                        let itemColors = isDark ? 'bg-white/[0.02] border-white/10' : 'bg-gray-50 border-gray-200';
                                                        if (isNew) itemColors = isDark ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-emerald-50 border-emerald-200';
                                                        else if (isMod) itemColors = isDark ? 'bg-amber-500/5 border-amber-500/20' : 'bg-amber-50 border-amber-200';
                                                        else if (isDel) itemColors = isDark ? 'bg-rose-500/5 border-rose-500/20' : 'bg-rose-50 border-rose-200';

                                                        const catIcons: Record<string, any> = {
                                                            'CAD': <Ruler className="w-5 h-5 text-indigo-500" />,
                                                            'Excel': <FileSpreadsheet className="w-5 h-5 text-emerald-500" />,
                                                            'Image': <Palette className="w-5 h-5 text-amber-500" />,
                                                            'Document': <FileText className="w-5 h-5 text-blue-500" />,
                                                            'SketchUp': <Layers className="w-5 h-5 text-rose-500" />,
                                                            'Presentation': <FileText className="w-5 h-5 text-orange-500" />,
                                                            'Video': <Activity className="w-5 h-5 text-cyan-500" />,
                                                        };
                                                        
                                                        const sizeStr = evt.size 
                                                            ? (evt.size > 1048576 ? `${(evt.size / 1048576).toFixed(1)}MB` : evt.size > 1024 ? `${(evt.size / 1024).toFixed(0)}KB` : `${evt.size}B`) 
                                                            : '';
                                                        
                                                        return (
                                                            <motion.div
                                                                key={i}
                                                                initial={{ opacity: 0, x: -10 }}
                                                                animate={{ opacity: 1, x: 0 }}
                                                                transition={{ delay: i * 0.05 }}
                                                            >
                                                              <div
                                                                className={`p-3 rounded-xl border flex items-start gap-3 ${itemColors} cursor-pointer`}
                                                                draggable={true}
                                                                onDragStart={(e) => {
                                                                    const fullPath = project?.exactNasPath ? `${project.exactNasPath}/${evt.filename}` : evt.filename;
                                                                    e.dataTransfer.setData('text/plain', JSON.stringify({ type: 'nas_file', path: fullPath, name: evt.filename }));
                                                                }}
                                                              >
                                                                <span className="flex items-center justify-center w-8 shrink-0">{catIcons[evt.category] || <Paperclip className="w-5 h-5 text-gray-500" />}</span>
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="flex items-center justify-between gap-2 flex-wrap">
                                                                        <div className="flex items-center gap-2">
                                                                            <button 
                                                                                onClick={() => handleFilePreview({ name: evt.filename, path: project?.exactNasPath ? `${project.exactNasPath}/${evt.filename}` : evt.filename })}
                                                                                className={`text-xs font-bold truncate hover:underline ${isDark ? 'text-gray-200 hover:text-indigo-400' : 'text-gray-800 hover:text-indigo-600'}`}
                                                                            >
                                                                                {evt.filename}
                                                                            </button>
                                                                            
                                                                            {!isListed && (
                                                                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                                                                                    isNew ? 'bg-emerald-500/20 text-emerald-400' 
                                                                                    : isMod ? 'bg-amber-500/20 text-amber-400'
                                                                                    : 'bg-rose-500/20 text-rose-400'
                                                                                }`}>
                                                                                    {isNew ? '신규등록' : isMod ? '최근수정' : '삭제'}
                                                                                </span>
                                                                            )}
                                                                            {isListed && (
                                                                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${isDark ? 'bg-gray-500/20 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                                                                                    파일 존재
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        
                                                                        <div className="flex items-center gap-1 opacity-60">
                                                                            <button 
                                                                                onClick={() => handleFileDownload({ name: evt.filename, path: project?.exactNasPath ? `${project.exactNasPath}/${evt.filename}` : evt.filename })}
                                                                                className={`p-1 rounded hover:bg-black/10 transition-colors`}
                                                                                title="다운로드"
                                                                            >
                                                                                <Copy className="w-3.5 h-3.5" />
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                    {evt.summary && (
                                                                        <p className={`text-[11px] mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{evt.summary}</p>
                                                                    )}
                                                                    <div className={`text-[10px] mt-2 flex items-center gap-3 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                                                                        {evt.created && evt.created !== 'unknown' && <span>생성: {new Date(evt.created).toLocaleString()}</span>}
                                                                        {evt.modified && evt.modified !== 'unknown' && <span className={isMod ? (isDark ? 'text-amber-500/80' : 'text-amber-600') : ''}>수정: {new Date(evt.modified).toLocaleString()}</span>}
                                                                        {sizeStr && <span>{sizeStr}</span>}
                                                                        {evt.timestamp && evt.timestamp !== 'unknown' && <span className="ml-auto">{timeAgo(evt.timestamp)}</span>}
                                                                    </div>
                                                                </div>
                                                              </div>
                                                            </motion.div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* ═══ FLOW SECTION ═══ */}
                                    {activeSection === 'flow' && (
                                        <div className="space-y-4">
                                            <h3 className={`text-lg font-bold flex items-center gap-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                                                <GitBranch className="w-5 h-5 text-blue-500" />
                                                Flow 태스크 동기화
                                                {flowProgress?.projectId && (
                                                    <span className={`text-[10px] font-normal px-2 py-0.5 rounded ${isDark ? 'bg-blue-500/10 text-blue-400' : 'bg-blue-50 text-blue-600'}`}>
                                                        {flowProgress.projectName}
                                                    </span>
                                                )}
                                            </h3>

                                            {/* Flow Progress Bar */}
                                            {flowProgress && flowProgress.total > 0 && (
                                                <div className={`p-4 rounded-xl border ${isDark ? 'bg-white/[0.02] border-white/10' : 'bg-gray-50 border-gray-200'}`}>
                                                    <div className="flex items-center justify-between mb-2">
                                                        <span className={`text-xs font-bold ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>전체 진행률</span>
                                                        <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{flowProgress.completionRate}%</span>
                                                    </div>
                                                    <div className="w-full h-3 rounded-full bg-gray-700/30 overflow-hidden">
                                                        <div
                                                            className="h-full rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 transition-all duration-500"
                                                            style={{ width: `${flowProgress.completionRate}%` }}
                                                        />
                                                    </div>
                                                    <div className="flex gap-4 mt-3 text-[11px]">
                                                        <span className={`flex items-center gap-1 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}><CheckCircle2 className="w-3 h-3" /> 완료 {flowProgress.done}</span>
                                                        <span className={`flex items-center gap-1 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}><RefreshCw className="w-3 h-3" /> 진행 {flowProgress.inProgress}</span>
                                                        <span className={`flex items-center gap-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}><Clock className="w-3 h-3" /> 대기 {flowProgress.todo}</span>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Flow Task List */}
                                            {flowProgress && flowProgress.recentTasks.length > 0 ? (
                                                <div className="space-y-1.5">
                                                    {flowProgress.recentTasks.map(task => (
                                                        <div
                                                            key={task.id}
                                                            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${isDark ? 'bg-white/[0.02] hover:bg-white/[0.04]' : 'bg-gray-50 hover:bg-gray-100'}`}
                                                        >
                                                            <div className="flex-1 min-w-0">
                                                                <span className={`text-xs font-medium truncate block ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                                                                    {task.title}
                                                                </span>
                                                                <div className="flex items-center gap-2 mt-1">
                                                                    {task.assignee && (
                                                                        <span className={`flex items-center gap-1 text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}><Users className="w-3 h-3" /> {task.assignee}</span>
                                                                    )}
                                                                    {task.dueDate && (
                                                                        <span className={`flex items-center gap-1 text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}><Clock className="w-3 h-3" /> {task.dueDate.slice(5)}</span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            {task.progress > 0 && (
                                                                <div className="w-16">
                                                                    <div className="h-1.5 rounded-full bg-gray-700/30 overflow-hidden">
                                                                        <div
                                                                            className="h-full rounded-full bg-blue-500"
                                                                            style={{ width: `${task.progress}%` }}
                                                                        />
                                                                    </div>
                                                                    <span className="text-[9px] text-gray-500">{task.progress}%</span>
                                                                </div>
                                                            )}
                                                            {statusBadge(task.status, isDark)}
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className={`text-center py-12 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                                    <GitBranch className="w-12 h-12 mx-auto mb-3 opacity-20" />
                                                    <p className="text-sm">Flow 태스크가 아직 없습니다.</p>
                                                    <p className="text-xs mt-1">Flow에서 이 프로젝트의 태스크를 등록하면 자동 동기화됩니다.</p>
                                                </div>
                                            )}

                                            {/* ── Flow 새 업무 요청 폼 ── */}
                                            {boardData?.flowProgress?.projectId && (
                                                <div className={`mt-6 p-4 rounded-xl border ${isDark ? 'bg-[#1a1d24] border-gray-700' : 'bg-white border-gray-200'}`}>
                                                    <h4 className={`text-xs font-bold mb-3 flex items-center gap-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                                        <Activity className="w-3.5 h-3.5 text-indigo-500" />
                                                        새 업무 요청 (Flow)
                                                    </h4>
                                                    <div className="flex flex-col gap-2">
                                                        <div className="flex items-center gap-2">
                                                            <select
                                                                value={newTaskAssignee}
                                                                onChange={(e) => setNewTaskAssignee(e.target.value)}
                                                                className={`text-[11px] font-bold px-2 py-1.5 rounded outline-none w-32 cursor-pointer
                                                                    ${isDark ? 'bg-black/20 border-gray-700 text-gray-300' : 'bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-100'}
                                                                    transition-colors border`}
                                                            >
                                                                <option value="" disabled hidden>담당자 지정</option>
                                                                {TEAM_MEMBERS.map(m => (
                                                                    <option key={`assign-flow-${m.id}`} value={m.name}>{m.name}</option>
                                                                ))}
                                                            </select>
                                                            <input
                                                                value={newTaskTitle}
                                                                onChange={(e) => setNewTaskTitle(e.target.value)}
                                                                placeholder="요청할 시스템/업무 제목을 입력하세요."
                                                                className={`flex-1 text-[11px] px-3 py-1.5 rounded border outline-none ${isDark ? 'bg-black/20 border-gray-700 text-gray-300 focus:border-indigo-500' : 'bg-white border-gray-300 focus:border-indigo-500'}`}
                                                                onKeyDown={e => e.key === 'Enter' && handleCreateFlowTask()}
                                                            />
                                                        </div>
                                                        <div className="flex justify-end mt-1">
                                                            <button
                                                                onClick={handleCreateFlowTask}
                                                                disabled={taskCreating || !newTaskTitle.trim() || !newTaskAssignee}
                                                                className={`px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all flex items-center gap-1.5 
                                                                    ${(taskCreating || !newTaskTitle.trim() || !newTaskAssignee)
                                                                        ? 'opacity-50 cursor-not-allowed bg-gray-500/10 text-gray-500' 
                                                                        : (isDark ? 'bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30 border border-indigo-500/30' : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100 border border-indigo-200')
                                                                    }`}
                                                            >
                                                                {taskCreating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                                                                Flow 업무 요청
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* ═══ FEED SECTION ═══ */}
                                    {activeSection === 'feed' && (
                                        <div className="space-y-4">
                                            {flowProgress?.projectId ? (
                                                <SchedulerProjectFeed projectId={flowProgress.projectId} isDark={isDark} />
                                            ) : (
                                                <div className={`text-center py-12 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                                    <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-20" />
                                                    <p className="text-sm">연결된 Flow 프로젝트가 없습니다.</p>
                                                    <p className="text-xs mt-1">이 스케줄 태스크에 매핑된 Flow 프로젝트를 찾을 수 없습니다.</p>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* ═══ Memo Board ═══ */}
                                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-800">
                                        <div className="flex items-center justify-between mb-3">
                                            <h3 className={`text-sm font-bold flex items-center gap-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                                                <FileText className="w-4 h-4 text-amber-500" />
                                                프로젝트 메모 (게시판)
                                            </h3>
                                        </div>

                                        {/* 작성 폼 */}
                                        <div className="relative mb-4">
                                            <div className="flex gap-2 mb-2">
                                                <select
                                                    value={memoAuthor}
                                                    onChange={(e) => setMemoAuthor(e.target.value)}
                                                    className={`text-[11px] font-bold px-2 py-1 rounded outline-none w-32 cursor-pointer
                                                        ${isDark ? 'bg-[#1a1d24] border-gray-700 text-gray-300' : 'bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-100'}
                                                        transition-colors border`}
                                                >
                                                    <option value="" disabled hidden>작성자 선택...</option>
                                                    {TEAM_MEMBERS.map(m => (
                                                        <option key={m.id} value={m.name}>{m.name}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <textarea
                                                value={newMemo}
                                                onChange={(e) => setNewMemo(e.target.value)}
                                                className={`w-full h-20 p-3 pr-24 rounded-xl border outline-none resize-none transition-colors text-[11px] ${
                                                    isDark 
                                                    ? 'bg-[#1a1d24] border-gray-700 text-gray-300 focus:border-amber-500' 
                                                    : 'bg-white border-gray-300 text-gray-700 focus:border-amber-500'
                                                }`}
                                                placeholder={memoAuthor ? `${memoAuthor}님, 프로젝트와 관련된 노트를 작성하세요.` : '작성자를 먼저 선택해주세요.'}
                                                disabled={!memoAuthor}
                                            />
                                            <button
                                                onClick={saveMemoToServer}
                                                disabled={!newMemo.trim() || !memoAuthor}
                                                className={`absolute bottom-3 right-3 px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all flex items-center gap-1.5 
                                                    ${(!newMemo.trim() || !memoAuthor)
                                                        ? 'opacity-50 cursor-not-allowed bg-gray-500/10 text-gray-500' 
                                                        : (isDark ? 'bg-amber-500/10 text-amber-500 hover:bg-amber-500/20 border border-amber-500/20' : 'bg-amber-50 text-amber-600 hover:bg-amber-100 border border-amber-200')
                                                    }`}
                                            >
                                                <Save className="w-3.5 h-3.5" /> 저장
                                            </button>
                                        </div>

                                        {/* 메모 리스트 */}
                                        <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">
                                            {memos.length === 0 ? (
                                                <div className={`text-center py-6 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                                    등록된 메모가 없습니다.
                                                </div>
                                            ) : (
                                                memos.map(m => (
                                                    <div key={m.id} className={`p-3 rounded-lg border relative group ${isDark ? 'bg-white/[0.02] border-white/10' : 'bg-gray-50 border-gray-200'}`}>
                                                        <div className="flex justify-between items-start mb-2">
                                                            <span className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                                                {new Date(m.timestamp).toLocaleString('ko-KR')}
                                                            </span>
                                                            {editingMemoId !== m.id && (
                                                                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                    <button onClick={() => copyToClipboard(m.id, m.text)} className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500`} title="복붙">
                                                                        {copiedId === m.id ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                                                                    </button>
                                                                    <button onClick={() => { setEditingMemoId(m.id); setEditMemoText(m.text); }} className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-blue-500`} title="수정">
                                                                        <Edit2 className="w-3.5 h-3.5" />
                                                                    </button>
                                                                    <button onClick={() => deleteMemoToServer(m.id)} className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-rose-500`} title="삭제">
                                                                        <Trash2 className="w-3.5 h-3.5" />
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                        {editingMemoId === m.id ? (
                                                            <div className="space-y-2">
                                                                <textarea
                                                                    value={editMemoText}
                                                                    onChange={e => setEditMemoText(e.target.value)}
                                                                    className={`w-full h-20 p-2 rounded border outline-none resize-none text-xs ${isDark ? 'bg-black/20 border-gray-700 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`}
                                                                />
                                                                <div className="flex justify-end gap-2">
                                                                    <button onClick={() => setEditingMemoId(null)} className="text-[10px] px-2 py-1 bg-gray-500/20 rounded">취소</button>
                                                                    <button onClick={() => updateMemoToServer(m.id)} className="text-[10px] px-2 py-1 bg-blue-500/20 text-blue-500 rounded font-bold">수정 완료</button>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className={`text-xs whitespace-pre-wrap leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                                                {m.text}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Handle 1: Before NAS (Left - Middle) */}
                        {showNasBrowser && (
                            <div
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    const startX = e.clientX;
                                    const startWidth = nasWidth;
                                    const onMouseMove = (moveEvent: MouseEvent) => {
                                        setNasWidth(Math.max(250, Math.min(800, startWidth - (moveEvent.clientX - startX))));
                                    };
                                    const onMouseUp = () => {
                                        document.removeEventListener('mousemove', onMouseMove);
                                        document.removeEventListener('mouseup', onMouseUp);
                                    };
                                    document.addEventListener('mousemove', onMouseMove);
                                    document.addEventListener('mouseup', onMouseUp);
                                }}
                                className="w-[6px] shrink-0 cursor-col-resize bg-transparent hover:bg-indigo-500/30 transition-colors z-20 flex flex-col justify-center items-center group"
                            >
                                <div className="h-10 w-1 rounded-full bg-gray-300 dark:bg-gray-700 group-hover:bg-indigo-400 transition-colors" />
                            </div>
                        )}

                        {/* ═══ Middle Panel (NAS Browser) ═══ */}
                        {showNasBrowser && (
                            <div 
                                className={`relative flex flex-col min-h-0 shrink-0 border-l ${isDark ? 'border-gray-800 bg-[#0b0d13]' : 'border-gray-200 bg-gray-50'}`}
                                style={{ width: `${nasWidth}px` }}
                            >
                                <div className={`p-4 flex items-center justify-between shadow-sm z-10 ${isDark ? 'border-gray-800 bg-[#11131a]' : 'border-gray-200 bg-white'}`}>
                                    <h4 className={`text-sm font-bold flex items-center gap-2 ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>
                                        <FolderSearch className="w-4 h-4" /> NAS 탐색기
                                    </h4>
                                    <button onClick={() => setShowNasBrowser(false)} className={`p-1.5 rounded-lg ${isDark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-black/5 text-gray-500'}`}>
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                                <div className="flex-1 overflow-y-auto p-3 custom-scrollbar">
                                    <div className={`mb-3 p-2 rounded border text-[11px] ${isDark ? 'bg-purple-500/10 border-purple-500/20 text-purple-300' : 'bg-purple-50 border-purple-200 text-purple-700'}`}>
                                        탐색기에서 원하는 파일을 선택 후 오른쪽 통합 RAG로 <b>Drag & Drop</b> 하세요!
                                    </div>
                                    
                                    {/* 상위 폴더 이동 버튼 */}
                                    {currentNasPath && (
                                        <button 
                                            onClick={() => {
                                                const parts = currentNasPath.split('/').filter(Boolean);
                                                parts.pop();
                                                browseNasFolders(parts.join('/'));
                                            }}
                                            className={`mb-3 flex items-center gap-2 px-3 py-2 rounded-lg text-[11px] font-bold w-full transition-colors border ${
                                                isDark ? 'bg-[#151921] hover:bg-[#1f2430] border-gray-800 text-gray-300' : 'bg-white hover:bg-gray-50 border-gray-200 text-gray-700 shadow-sm'
                                            }`}
                                        >
                                            <ChevronLeft className="w-4 h-4" />
                                            <span>상위 폴더로 이동</span>
                                            <span className="opacity-40 ml-auto mr-1 truncate max-w-[150px] font-normal">{currentNasPath}</span>
                                        </button>
                                    )}
                                    <NasTreeRenderer 
                                        folders={nasFolders}
                                        files={nasFiles}
                                        depth={0}
                                        isDark={isDark}
                                        nasTreeCache={nasTreeCache}
                                        nasExpandedPaths={nasExpandedPaths}
                                        nasLoadingPaths={nasLoadingPaths}
                                        toggleTreeFolder={toggleTreeFolder}
                                        linkNasFolder={linkNasFolder}
                                        handleFilePreview={handleFilePreview}
                                        nasSearchQuery={nasSearchQuery}
                                        onContextMenu={(e, path, name) => {
                                            e.preventDefault();
                                            setNasContextMenu({ x: e.clientX, y: e.clientY, path, name });
                                        }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Handle 2: Before Chat (Middle - Right or Left - Right) */}
                        <div
                            onMouseDown={(e) => {
                                e.preventDefault();
                                const startX = e.clientX;
                                const startWidth = chatWidth;
                                const onMouseMove = (moveEvent: MouseEvent) => {
                                    setChatWidth(Math.max(300, Math.min(1200, startWidth - (moveEvent.clientX - startX))));
                                };
                                const onMouseUp = () => {
                                    document.removeEventListener('mousemove', onMouseMove);
                                    document.removeEventListener('mouseup', onMouseUp);
                                };
                                document.addEventListener('mousemove', onMouseMove);
                                document.addEventListener('mouseup', onMouseUp);
                            }}
                            className="w-[6px] shrink-0 cursor-col-resize bg-transparent hover:bg-indigo-500/30 transition-colors z-20 flex flex-col justify-center items-center group"
                        >
                            <div className="h-10 w-1 rounded-full bg-gray-300 dark:bg-gray-700 group-hover:bg-indigo-400 transition-colors" />
                        </div>

                        {/* ═══ AI Agent RAG Chat (Expanded) ═══ */}
                        <div 
                            className={`relative flex flex-col border-l shrink-0 min-h-0 ${isDark ? 'bg-[#0d0f14] border-gray-800' : 'bg-gray-50 border-gray-200'}`}
                            style={{ width: `${chatWidth}px` }}
                        >
                            <div className={`p-4 flex items-center justify-between shadow-sm z-10 shrink-0 ${isDark ? 'border-gray-800 bg-[#11131a]' : 'border-gray-200 bg-white'}`}>
                                <div className="flex items-center gap-2">
                                    <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
                                    <span className={`text-sm font-bold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>OpenClaw 게이트웨이</span>
                                </div>
                                <span className={`text-[10px] px-2 py-0.5 rounded-full ${isDark ? 'bg-indigo-500/20 text-indigo-400' : 'bg-indigo-100 text-indigo-600'}`}>
                                    {activeProject || projectId || project?.nasIdPrefix}
                                </span>
                            </div>
                            
                            {/* Embed OmniChatPanel full size */}
                            <div className="flex-1 relative bg-white dark:bg-[#0a0a12] min-h-0 overflow-hidden rounded-br-2xl">
                                <OmniChatPanel 
                                    projectId={project?.nasIdPrefix || projectId || activeProject} 
                                    taskTitle={taskTitle}
                                    compact={false} 
                                    className="!h-full !border-0 !rounded-none"
                                    externalNasPath={chatNasPath}
                                    onNasPathLoaded={() => setChatNasPath(null)}
                                    onRequestNasBrowser={() => {
                                        // The user requested to delete the redundant internal explorer, 
                                        // so this now acts as a placeholder or can link to the main explorer.
                                        let pathStr = '';
                                        if (boardData?.project?.exactNasPath) {
                                            pathStr = boardData.project.exactNasPath;
                                        } else if (boardData?.project?.nasId) {
                                            const yyMatch = boardData.project.nasId.match(/^ID_(\d{2})/);
                                            const yy = yyMatch ? yyMatch[1] : '25';
                                            pathStr = `B_DESIGN/[20${yy}_PROJECT]/${boardData.project.nasId}`;
                                        }
                                        browseNasFolders(pathStr, `B_DESIGN/${boardData?.project?.nasId}`);
                                    }}
                                />
                            </div>
                        </div>

                    </div>
                </motion.div>
            </div>

            {/* NAS Context Menu Overlay */}
            {nasContextMenu && (
                <>
                    <div className="fixed inset-0 z-[200]" onClick={() => { setNasContextMenu(null); setNasRenaming(null); }} onContextMenu={(e) => { e.preventDefault(); setNasContextMenu(null); }} />
                    <div 
                        className={`fixed z-[210] py-1.5 rounded-xl shadow-2xl border w-60 overflow-hidden ${isDark ? 'bg-[#1e1e24] border-gray-700' : 'bg-white border-gray-200'}`}
                        style={{ top: Math.min(nasContextMenu.y, window.innerHeight - 280), left: Math.min(nasContextMenu.x, window.innerWidth - 250) }}
                    >
                        {/* 파일명 헤더 */}
                        <div className={`px-3 py-2 mb-0.5 border-b flex items-center gap-2 ${isDark ? 'text-gray-300 border-gray-800 bg-white/[0.02]' : 'text-gray-600 border-gray-100 bg-gray-50'}`}>
                            {nasContextMenu.isFolder
                                ? <span className="text-yellow-500 text-sm">📁</span>
                                : <span className="text-blue-500 text-sm">📄</span>}
                            <span className="text-[11px] font-bold truncate flex-1">{nasContextMenu.name}</span>
                        </div>

                        {/* 이름 바꾸기 */}
                        {nasRenaming?.path === nasContextMenu.path ? (
                            <div className="px-3 py-2">
                                <input
                                    autoFocus
                                    value={renameValue}
                                    onChange={e => setRenameValue(e.target.value)}
                                    onKeyDown={async (e) => {
                                        if (e.key === 'Enter' && renameValue.trim()) {
                                            const parentPath = nasContextMenu.path.split('/').slice(0, -1).join('/');
                                            const newPath = parentPath + '/' + renameValue.trim();
                                            try {
                                                await fetch(`${process.env.NEXT_PUBLIC_NAS_GATEWAY}/api/file-rename`, {
                                                    method: 'POST',
                                                    headers: { 'Content-Type': 'application/json', 'Bypass-Tunnel-Reminder': 'true' },
                                                    body: JSON.stringify({ from: nasContextMenu.path, to: newPath })
                                                });
                                                if (currentNasPath) browseNasFolders(currentNasPath);
                                            } catch { /* 실패 시 무시 */ }
                                            setNasRenaming(null);
                                            setNasContextMenu(null);
                                        }
                                        if (e.key === 'Escape') { setNasRenaming(null); setNasContextMenu(null); }
                                    }}
                                    className={`w-full text-[11px] px-2 py-1.5 rounded border outline-none ${
                                        isDark ? 'bg-black/30 border-indigo-500 text-white' : 'bg-white border-indigo-400 text-gray-900'
                                    }`}
                                    placeholder="새 이름 입력 후 Enter"
                                />
                            </div>
                        ) : (
                            <button
                                className={`w-full text-left px-4 py-2 text-[11px] font-medium flex items-center gap-2.5 transition-colors ${isDark ? 'hover:bg-indigo-500/20 text-gray-300' : 'hover:bg-indigo-50 text-gray-700'}`}
                                onClick={() => {
                                    setNasRenaming({ path: nasContextMenu.path, name: nasContextMenu.name });
                                    setRenameValue(nasContextMenu.name);
                                }}
                            >
                                <span className="text-indigo-500">✏️</span> 이름 바꾸기
                            </button>
                        )}

                        {/* 복사 */}
                        <button
                            className={`w-full text-left px-4 py-2 text-[11px] font-medium flex items-center gap-2.5 transition-colors ${isDark ? 'hover:bg-blue-500/20 text-gray-300' : 'hover:bg-blue-50 text-gray-700'}`}
                            onClick={() => {
                                setNasClipboard({ type: 'copy', path: nasContextMenu.path, name: nasContextMenu.name });
                                navigator.clipboard.writeText(nasContextMenu.path).catch(() => {});
                                setNasContextMenu(null);
                            }}
                        >
                            <span className="text-blue-500">📋</span>
                            복사
                            {nasClipboard?.type === 'copy' && nasClipboard.path === nasContextMenu.path && <span className="ml-auto text-[9px] text-blue-400">✓클립보드</span>}
                        </button>

                        {/* 자르기 */}
                        <button
                            className={`w-full text-left px-4 py-2 text-[11px] font-medium flex items-center gap-2.5 transition-colors ${isDark ? 'hover:bg-amber-500/20 text-gray-300' : 'hover:bg-amber-50 text-gray-700'}`}
                            onClick={() => {
                                setNasClipboard({ type: 'cut', path: nasContextMenu.path, name: nasContextMenu.name });
                                setNasContextMenu(null);
                            }}
                        >
                            <span className="text-amber-500">✂️</span>
                            자르기
                            {nasClipboard?.type === 'cut' && nasClipboard.path === nasContextMenu.path && <span className="ml-auto text-[9px] text-amber-400">✓대기중</span>}
                        </button>

                        <hr className={`my-1 ${isDark ? 'border-gray-800' : 'border-gray-100'}`} />

                        {/* 절대경로 복사 (Agent) */}
                        <button
                            className={`w-full text-left px-4 py-2 text-[11px] font-medium flex items-center gap-2.5 transition-colors ${isDark ? 'hover:bg-blue-500/20 text-gray-300' : 'hover:bg-blue-50 text-gray-700'}`}
                            onClick={() => {
                                navigator.clipboard.writeText(nasContextMenu.path);
                                setNasContextMenu(null);
                            }}
                        >
                            <span className="text-cyan-500">🔗</span> 절대 경로 복사 (Agent 분석용)
                        </button>

                        {/* 공유 */}
                        <button
                            className={`w-full text-left px-4 py-2 text-[11px] font-medium flex items-center gap-2.5 transition-colors ${isDark ? 'hover:bg-emerald-500/20 text-gray-300' : 'hover:bg-emerald-50 text-gray-700'}`}
                            onClick={() => {
                                const shareUrl = `${process.env.NEXT_PUBLIC_NAS_GATEWAY}/api/file-preview?path=${encodeURIComponent(nasContextMenu.path)}`;
                                if (navigator.share) {
                                    navigator.share({ title: nasContextMenu.name, url: shareUrl }).catch(() => {});
                                } else {
                                    navigator.clipboard.writeText(shareUrl);
                                    alert('공유 링크가 클립보드에 복사되었습니다.');
                                }
                                setNasContextMenu(null);
                            }}
                        >
                            <span className="text-emerald-500">🔗</span> 공유
                        </button>

                        {/* 파일 다운로드 */}
                        <button
                            className={`w-full text-left px-4 py-2 text-[11px] font-medium flex items-center gap-2.5 transition-colors text-amber-600 dark:text-amber-400 ${isDark ? 'hover:bg-amber-500/20' : 'hover:bg-amber-50'}`}
                            onClick={() => {
                                handleFileDownload({ name: nasContextMenu.name, path: nasContextMenu.path });
                                setNasContextMenu(null);
                            }}
                        >
                            <span>⬇️</span> 파일 다운로드
                        </button>

                        <hr className={`my-1 ${isDark ? 'border-gray-800' : 'border-gray-100'}`} />

                        {/* 삭제 */}
                        <button
                            className={`w-full text-left px-4 py-2 text-[11px] font-medium flex items-center gap-2.5 transition-colors text-rose-500 ${isDark ? 'hover:bg-rose-500/20' : 'hover:bg-rose-50'}`}
                            onClick={async () => {
                                if (!confirm(`"${nasContextMenu.name}" 을(를) 삭제하시겠습니까?\n⚠️ NAS에서 영구 삭제됩니다.`)) { setNasContextMenu(null); return; }
                                try {
                                    await fetch(`${process.env.NEXT_PUBLIC_NAS_GATEWAY}/api/file-delete`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json', 'Bypass-Tunnel-Reminder': 'true' },
                                        body: JSON.stringify({ path: nasContextMenu.path })
                                    });
                                    if (currentNasPath) browseNasFolders(currentNasPath);
                                } catch { /* gateway 미지원 시 무시 */ }
                                setNasContextMenu(null);
                            }}
                        >
                            <span>🗑️</span> 삭제
                        </button>
                    </div>
                </>
            )}
        </AnimatePresence>
    );
}
